/**
 * Les absences à gérer : qui manque, quel jour, sur quel chantier.
 *
 * *Demande du métier, 16/08/2026 : « lorsque des chantiers seront déjà organisés avec
 * des personnes affectées et que survient une absence maladie ou congé, il faudra faire
 * un tableau de bord qui se met automatiquement à jour pour le responsable
 * d'exploitation, qui lui indique le chantier puis la journée ou les journées et la
 * personne concernée. »*
 *
 * ## Il n'y a rien à mettre à jour : c'est un croisement, pas un état
 *
 * Ce tableau ne stocke rien et ne se déclenche sur rien. Il croise, à chaque affichage,
 * les affectations réelles du plan avec les absences du référentiel maître. Une absence
 * saisie ce matin apparaît donc au prochain chargement, sans qu'aucun traitement n'ait
 * eu à s'exécuter entre les deux — et sans qu'un oubli de déclenchement puisse laisser
 * une alerte dans l'ombre. C'est ce qui le rend « automatique » au sens le plus solide :
 * il ne peut pas être en retard sur les données, il EST les données.
 *
 * ## Ce qui n'est pas fait ici, et pourquoi
 *
 * **Rien n'est retiré, rien n'est remplacé.** L'affectation d'une personne absente reste
 * en place : la défaire d'autorité ferait croire le besoin couvert alors qu'il ne l'est
 * plus, et personne ne saurait qu'il y a eu quelque chose à décider. L'écran nomme le
 * problème ; le responsable arbitre — c'est la même règle que pour les conflits de
 * ressource.
 *
 * ## Congé validé, congé à valider : la distinction se voit partout
 *
 * *« La précision congé non validé ou validé est importante » (métier, 16/08/2026).* Un
 * congé validé est acquis, il faut remplacer la personne ; un congé demandé et non
 * encore validé est une décision qui appartient encore à l'entreprise — et le
 * responsable d'exploitation est parfois celui qui la prend. Les deux ne se rangent pas
 * dans la même colonne mentale, donc pas dans la même ligne d'écran.
 */
import { useMemo } from "react";
import type { JSX } from "react";
import { direLAbsence, estAcquise, formaterDate } from "@planif/moteur";
import type { Absence } from "@planif/moteur";
import type { BesoinDate, ChantierAuPlan } from "./api";

/*
  **Le vocabulaire des absences a été monté au moteur le 16/09/2026.**

  Il vivait ici, et trois lectures le nomment désormais — ce tableau, le planning des ressources,
  et les anomalies du plan de charge, dont la détection est au serveur. Une phrase d'écran ne
  pouvait pas servir le serveur ; la recopier aurait fabriqué deux vocabulaires pour la même
  absence. Les réexports gardent les imports existants valides : `direLAbsence` et `Absence` se
  prennent encore ici, et ne sont plus définis ici.
*/
export { direLAbsence, estAcquise };
export type { Absence };

/* ---------------------------------------------------------------------------
 * Le croisement
 * ------------------------------------------------------------------------- */

export type LigneAbsence = {
  readonly cle: string;
  readonly chantierId: string;
  readonly apo: string;
  readonly nomChantier: string;
  readonly personne: string;
  readonly absence: Absence;
  /** Les jours concernés, dans l'ordre. « La journée ou les journées » du métier. */
  readonly jours: readonly string[];
  /** Ce qui était prévu ces jours-là : le poste et le code, pour savoir quoi remplacer. */
  readonly besoins: readonly string[];
};

/**
 * Les affectations qui tombent un jour d'absence.
 *
 * **Toutes natures de besoin confondues**, y compris les rotations de transport : une
 * personne absente affectée à une rotation est un problème à gérer, et c'est
 * l'exploitation qui trouve les hommes. Se borner aux besoins en heures laisserait un
 * chauffeur absent invisible sur cet écran comme sur les autres.
 *
 * Le regroupement va jusqu'à la **nature de l'absence** : quelqu'un qui a un congé
 * validé lundi et un arrêt mardi produit deux lignes, parce que ce sont deux décisions
 * différentes.
 */
export function croiserLesAbsences(
  besoins: readonly BesoinDate[],
  chantiers: readonly ChantierAuPlan[],
  absences: ReadonlyMap<string, Absence>,
): readonly LigneAbsence[] {
  const connus = new Map(chantiers.map((c) => [c.chantierId, c]));
  const lignes = new Map<
    string,
    {
      chantierId: string;
      personne: string;
      absence: Absence;
      jours: Set<string>;
      besoins: Set<string>;
    }
  >();

  for (const besoin of besoins) {
    for (const affectation of besoin.affectations) {
      const absence = absences.get(`${affectation.ressourceId}#${besoin.date}`);
      if (absence === undefined) continue;

      const cle = `${besoin.chantierId}#${affectation.ressourceId}#${absence.nature}#${String(absence.valide)}`;
      const ligne = lignes.get(cle) ?? {
        chantierId: besoin.chantierId,
        personne: affectation.libelle,
        absence,
        jours: new Set<string>(),
        besoins: new Set<string>(),
      };
      ligne.jours.add(besoin.date);
      ligne.besoins.add(`${besoin.poste} · ${besoin.code}`);
      lignes.set(cle, ligne);
    }
  }

  return [...lignes.entries()]
    .map(([cle, ligne]) => {
      const chantier = connus.get(ligne.chantierId);
      return {
        cle,
        chantierId: ligne.chantierId,
        apo: chantier?.apo ?? "?",
        nomChantier: chantier?.nom ?? "",
        personne: ligne.personne,
        absence: ligne.absence,
        jours: [...ligne.jours].sort(),
        besoins: [...ligne.besoins].sort(),
      };
    })
    // Le plus tôt d'abord : c'est l'ordre dans lequel il faut décider. À jour égal, les
    // absences acquises avant celles qui restent à confirmer — les premières demandent un
    // remplaçant, les secondes une décision.
    .sort(
      (a, b) =>
        (a.jours[0] ?? "").localeCompare(b.jours[0] ?? "") ||
        Number(estAcquise(b.absence)) - Number(estAcquise(a.absence)) ||
        a.apo.localeCompare(b.apo),
    );
}

/**
 * Les jours en clair, en repliant les suites continues.
 *
 * « du 07/09 au 11/09 » plutôt que cinq dates : un arrêt de trois semaines écrirait
 * quinze dates sur une ligne, et on ne verrait plus ni le début ni la fin. La continuité
 * se juge sur les jours **affichés** — ceux où la personne était affectée —, donc un
 * week-end ne coupe pas une suite, et une reprise le mercredi la coupe.
 */
export function direLesJours(jours: readonly string[]): string {
  if (jours.length === 0) return "";
  const morceaux: string[] = [];
  let debut = jours[0] ?? "";
  let precedent = debut;

  const fermer = (): void => {
    morceaux.push(
      debut === precedent
        ? formaterDate(debut)
        : `du ${formaterDate(debut)} au ${formaterDate(precedent)}`,
    );
  };

  for (const jour of jours.slice(1)) {
    // Une suite ne se juge pas sur « le lendemain » : ce sont les jours affectés qui se
    // suivent, pas les dates. Deux jours consécutifs dans la liste sont donc considérés
    // comme une suite dès lors qu'ils sont à moins de quatre jours l'un de l'autre — le
    // saut d'un week-end, pont compris.
    if (ecartEnJours(precedent, jour) <= 4) {
      precedent = jour;
      continue;
    }
    fermer();
    debut = jour;
    precedent = jour;
  }
  fermer();
  return morceaux.join(", ");
}

/** L'écart en jours de calendrier entre deux dates, par leur seule écriture. */
function ecartEnJours(a: string, b: string): number {
  const jours = (date: string): number => {
    const annee = Number(date.slice(0, 4));
    const mois = Number(date.slice(5, 7));
    const jour = Number(date.slice(8, 10));
    // L'algorithme du moteur, réduit : aucune date-objet, donc aucun fuseau.
    const an = annee - (mois <= 2 ? 1 : 0);
    const ere = Math.floor(an / 400);
    const dansLEre = an - ere * 400;
    const dansLAnnee = Math.floor((153 * (mois + (mois > 2 ? -3 : 9)) + 2) / 5) + jour - 1;
    return (
      ere * 146097 +
      dansLEre * 365 +
      Math.floor(dansLEre / 4) -
      Math.floor(dansLEre / 100) +
      dansLAnnee
    );
  };
  return jours(b) - jours(a);
}

/* ---------------------------------------------------------------------------
 * Le rendu
 * ------------------------------------------------------------------------- */

export function AbsencesAGerer({
  besoins,
  chantiers,
  absences,
}: {
  besoins: readonly BesoinDate[];
  chantiers: readonly ChantierAuPlan[];
  absences: ReadonlyMap<string, Absence>;
}): JSX.Element {
  const lignes = useMemo(
    () => croiserLesAbsences(besoins, chantiers, absences),
    [besoins, chantiers, absences],
  );

  if (lignes.length === 0) {
    return (
      <p className="etat">
        Aucune personne affectée n'est absente sur la période. Ce tableau se recalcule à
        chaque affichage : il n'y a rien à déclencher.
      </p>
    );
  }

  return (
    <table className="ressources absences">
      <thead>
        <tr>
          <th>Chantier</th>
          <th>Personne</th>
          <th>Absence</th>
          <th>Jour(s)</th>
          <th>Ce qui était prévu</th>
        </tr>
      </thead>
      <tbody>
        {lignes.map((ligne) => (
          <tr
            key={ligne.cle}
            className={estAcquise(ligne.absence) ? "absences--acquise" : undefined}
          >
            <td>
              <span className="code">{ligne.apo}</span>
              <span className="note"> {ligne.nomChantier}</span>
            </td>
            <td>{ligne.personne}</td>
            <td>
              <span className={estAcquise(ligne.absence) ? "en-ecart" : "note"}>
                {direLAbsence(ligne.absence)}
              </span>
            </td>
            <td>{direLesJours(ligne.jours)}</td>
            <td className="note">{ligne.besoins.join(", ")}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
